import React, { useState, useEffect } from "react";
import AdminSidebar from "../../component/AdminComp/AdminSidebar";
import AdminTopbar from "../../component/AdminComp/AdminTopbar";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { baseurl } from "../../Baseurl";
import Select from "react-select";

const EditEducationProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL

  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    about: "",
    youtube_link: "",
    category_ids: [], // array of selected category ids
  });

  const [selectedLevel1, setSelectedLevel1] = useState([]);
  const [selectedLevel2, setSelectedLevel2] = useState([]);
  const [selectedLevel3, setSelectedLevel3] = useState([]);

  const [level1Options, setLevel1Options] = useState([]);
  const [level2Options, setLevel2Options] = useState([]);
  const [level3Options, setLevel3Options] = useState([]);

  const [pdfFile, setPdfFile] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) fetchProfileData();
  }, [categories]);

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllEducationCategories`);
      const cats = res.data.data || [];
      setCategories(cats);

      // Prepare level1 options (parents)
      setLevel1Options(
        cats.filter((cat) => !cat.parent_id).map((cat) => ({
          value: cat.id,
          label: cat.education_title,
        }))
      );
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  // Fetch profile data
  const fetchProfileData = async () => {
    try {
      const res = await axios.get(`${baseurl}getEducationProfileById/${id}`);
      const data = res.data;

      // Update form data
      setFormData({
        name: data.name || "",
        about: data.about || "",
        youtube_link: data.youtube_link || "",
        category_ids: data.category_id || [], // expecting array from backend
      });

      // Preselect categories in respective levels
      const level1Selected = categories
        .filter(
          (cat) =>
            !cat.parent_id &&
            (data.category_id || []).some((id) => id === cat.id)
        )
        .map((cat) => ({ value: cat.id, label: cat.education_title }));
      setSelectedLevel1(level1Selected);

      const level2Selected = categories
        .filter(
          (cat) =>
            cat.parent_id &&
            (data.category_id || []).some((id) => id === cat.id) &&
            level1Selected.some((lvl1) => lvl1.value === cat.parent_id)
        )
        .map((cat) => ({ value: cat.id, label: cat.education_title }));
      setSelectedLevel2(level2Selected);

      const level3Selected = categories
        .filter(
          (cat) =>
            cat.parent_id &&
            (data.category_id || []).some((id) => id === cat.id) &&
            level2Selected.some((lvl2) => lvl2.value === cat.parent_id)
        )
        .map((cat) => ({ value: cat.id, label: cat.education_title }));
      setSelectedLevel3(level3Selected);

      // Prepare level2 & level3 options
      const level2Opts = categories
        .filter((cat) => cat.parent_id && level1Selected.some((l1) => l1.value === cat.parent_id))
        .map((cat) => ({ value: cat.id, label: cat.education_title }));
      setLevel2Options(level2Opts);

      const level3Opts = categories
        .filter((cat) => cat.parent_id && level2Selected.some((l2) => l2.value === cat.parent_id))
        .map((cat) => ({ value: cat.id, label: cat.education_title }));
      setLevel3Options(level3Opts);

    } catch (err) {
      console.error("Error fetching profile", err);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // File change
  const handleFileChange = (e) => {
    setPdfFile(e.target.files[0]);
  };

  // Level change handlers
  const handleLevel1Change = (options) => {
    setSelectedLevel1(options || []);
    setSelectedLevel2([]);
    setSelectedLevel3([]);

    // Level2 options
    const level2Opts = categories
      .filter((cat) => cat.parent_id && (options || []).some((o) => o.value === cat.parent_id))
      .map((cat) => ({ value: cat.id, label: cat.education_title }));
    setLevel2Options(level2Opts);
    setLevel3Options([]);

    // Update selected category_ids
    const ids = (options || []).map((o) => o.value);
    setFormData((prev) => ({ ...prev, category_ids: ids }));
  };

  const handleLevel2Change = (options) => {
    setSelectedLevel2(options || []);
    setSelectedLevel3([]);

    // Level3 options
    const level3Opts = categories
      .filter((cat) => cat.parent_id && (options || []).some((o) => o.value === cat.parent_id))
      .map((cat) => ({ value: cat.id, label: cat.education_title }));
    setLevel3Options(level3Opts);

    // Combine level1 + level2 ids
    const ids = [
      ...selectedLevel1.map((o) => o.value),
      ...(options || []).map((o) => o.value),
    ];
    setFormData((prev) => ({ ...prev, category_ids: ids }));
  };

  const handleLevel3Change = (options) => {
    setSelectedLevel3(options || []);

    // Combine all level ids
    const ids = [
      ...selectedLevel1.map((o) => o.value),
      ...selectedLevel2.map((o) => o.value),
      ...(options || []).map((o) => o.value),
    ];
    setFormData((prev) => ({ ...prev, category_ids: ids }));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) data.append(key, JSON.stringify(value));
      else data.append(key, value);
    });

    if (pdfFile) data.append("pdf", pdfFile);

    // Log for debugging
    console.log("Submitting form data:");
    for (let pair of data.entries()) {
      console.log(pair[0], pair[1]);
    }

    try {
      await axios.put(`${baseurl}updateEducationProfileById/${id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Profile updated successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate("/admin/all-education"); // redirect to all education
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update profile.",
      });
    }
  };

  return (
    <div className="App" style={{ display: "flex" }}>
      <AdminSidebar />
      <div className="content" style={{ flexGrow: 1, padding: "20px" }}>
        <AdminTopbar />
        <div className="container-fluid">
          <h4 className="mb-3">Edit Education</h4>

          <div className="card p-4">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="row g-3">
                <div className="col-md-12">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Name"
                    required
                  />
                </div>

                <div className="col-md-12">
                  <textarea
                    name="about"
                    value={formData.about}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Write About Yourself"
                    rows={4}
                    required
                  />
                </div>

                <div className="col-md-12">
                  <input
                    type="text"
                    name="youtube_link"
                    value={formData.youtube_link}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Enter YouTube Link"
                  />
                </div>

                {/* React-Select dropdowns */}
                <div className="col-md-4">
                  <label className="form-label">What We Do</label>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    options={level1Options}
                    value={selectedLevel1}
                    onChange={handleLevel1Change}
                  />
                </div>

                {level2Options.length > 0 && (
                  <div className="col-md-4">
                    <label className="form-label">Category</label>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      options={level2Options}
                      value={selectedLevel2}
                      onChange={handleLevel2Change}
                    />
                  </div>
                )}

                {level3Options.length > 0 && (
                  <div className="col-md-4">
                    <label className="form-label">Sub-Category</label>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      options={level3Options}
                      value={selectedLevel3}
                      onChange={handleLevel3Change}
                    />
                  </div>
                )}

                <div className="col-md-12">
                  <label className="form-label">Upload PDF Document</label>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="form-control"
                    onChange={handleFileChange}
                  />
                </div>

                <div className="col-12 mt-3">
                  <button type="submit" className="btn btn-primary w-100">
                    Update Profile
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEducationProfile;
